import { Component, OnInit } from '@angular/core';
import {
  AbstractControl,
  UntypedFormControl,
  UntypedFormGroup,
  ValidationErrors,
  ValidatorFn,
  Validators,
} from '@angular/forms';
import { finalize } from 'rxjs';
import { ChangePasswordRequest } from 'src/app/models/auth/change-password-request.model';
import { PasswordPolicyResponse } from 'src/app/models/auth/password-policy-response.model';
import { LocalStorageService } from 'src/app/core/services/local-storage.service';
import { AuthService } from 'src/app/services/auth.service';
import { ErrorMessage } from 'src/app/shared/models/error.model';
import { requireDigitValidator } from 'src/app/shared/directives/validators/require-digit-validator.directive';
import { requireNonAplhanumericValidator } from 'src/app/shared/directives/validators/require-non-aplhanumeric-validator.directive';
import { caseSizeValidator } from 'src/app/shared/directives/validators/case-validator.directive';

@Component({
  selector: 'change-password',
  templateUrl: './change-password.component.html',
  standalone: false,
})
export class ChangePasswordComponent implements OnInit {
  changePasswordRequest: ChangePasswordRequest;
  changePasswordForm: UntypedFormGroup = new UntypedFormGroup({});

  error: ErrorMessage | undefined;

  changeDone: boolean = false;
  changingPassword: boolean = false;

  passwordPolicy: PasswordPolicyResponse;

  constructor(
    private readonly _authService: AuthService,
    private readonly _localStorageService: LocalStorageService,
  ) {
    this.changePasswordRequest = new ChangePasswordRequest('', '', '');
    this.changePasswordForm = new UntypedFormGroup(
      {
        oldPassword: new UntypedFormControl(
          this.changePasswordRequest.oldPassword,
        ),
        newPassword: new UntypedFormControl(
          this.changePasswordRequest.newPassword,
        ),
        confirmPassword: new UntypedFormControl(
          this.changePasswordRequest.confirmPassword,
        ),
      },
      {
        validators: (group: AbstractControl) =>
          this._matchStringValidator(group, 'newPassword', 'confirmPassword'),
      },
    );
  }

  // form controls
  get oldPassword() {
    return this.changePasswordForm.get('oldPassword');
  }
  get newPassword() {
    return this.changePasswordForm.get('newPassword');
  }
  get confirmPassword() {
    return this.changePasswordForm.get('confirmPassword');
  }

  ngOnInit(): void {
    const userCompanyGuid = this._localStorageService.authorizationData?.userCompanyGuid;
    if (userCompanyGuid) {
      this._authService.getPasswordPolicy(userCompanyGuid).subscribe({
        next: (policy) => {
          this.passwordPolicy = policy;
          this.applyPasswordValidators(policy);
        }
      });
    }
  }

  private applyPasswordValidators(policy: PasswordPolicyResponse): void {
    const validators: ValidatorFn[] = [Validators.required, Validators.minLength(policy.minimumPasswordLength || 8)];

    if (policy.requireUppercase && policy.requireLowercase) {
      validators.push(caseSizeValidator('both'));
    } else if (policy.requireUppercase) {
      validators.push(caseSizeValidator('upper'));
    } else if (policy.requireLowercase) {
      validators.push(caseSizeValidator('lower'));
    }

    if (policy.requireDigit) {
      validators.push(requireDigitValidator());
    }

    if (policy.requireSpecialCharacter) {
      validators.push(requireNonAplhanumericValidator());
    }

    this.newPassword.setValidators(validators);
    this.newPassword.updateValueAndValidity();
  }

  onChangePasswordClicked = () => {
    this.changePasswordForm.markAllAsTouched();
    if (!this.changePasswordForm.valid) return;

    this.error = null;
    this.changingPassword = true;
    this._authService
      .changePassword(this.changePasswordRequest)
      .pipe(
        finalize(() => {
          this.changingPassword = false;
        }),
      )
      .subscribe(
        (response) => {
          this.changeDone = true;
        },
        (err) => {
          this.changeDone = false;
          this.error = new ErrorMessage(
            'Unable to change password!',
            err.error,
          );
        },
      );
  };

  private _matchStringValidator = (
    group: AbstractControl,
    nameOne: string,
    nameTwo: string,
  ): ValidationErrors | null => {
    const firstValue = group.get(nameOne)?.value;
    const secondValue = group.get(nameTwo)?.value;
    return !firstValue || !secondValue || firstValue === secondValue
      ? null
      : { stringDoesntMatch: true };
  };
}
